import { type ReactNode } from 'react';
import { SAFE } from './BroadcastFrame.js';

/**
 * ProsceniumFrame — the persistent brand chrome that wraps the Stage inside a
 * BroadcastFrame: gold filigree corner marks, a film-grain overlay, a radial
 * vignette, and a bottom "lower-third" slot for phase/name captions. It renders
 * its `children` (the scene + screen) in the center and layers the chrome above.
 *
 * The lower-third slot is a PURE positioning container — the caption component
 * (LowerThird) draws its own translucent ink box, so nothing renders here when
 * no caption is showing.
 *
 * Every art-backed piece is a placeholder (CSS gradients / borders) until the
 * generated asset lands, at which point pass the matching `*Src` prop and the
 * placeholder is replaced by the image with zero layout change:
 *   cornerSrc  — a single top-left filigree corner (mirrored to all four)
 *   grainSrc   — a tiling film-grain texture (nothing renders until provided; a
 *                CSS noise fake reads as ugly banding, so we wait for real art)
 *
 * Sits inside BroadcastFrame's 1920×1080 design space, so all offsets are px.
 */

export interface ProsceniumFrameProps {
  children?: ReactNode;
  /** Top-left filigree corner art (mirrored into all four corners). */
  cornerSrc?: string;
  /** Tiling grain texture. Nothing renders until this is provided. */
  grainSrc?: string;
  /** Lower-third content (phase label, name, caption) shown at the bottom. */
  lowerThird?: ReactNode;
  className?: string;
  style?: React.CSSProperties;
}

export function ProsceniumFrame({
  children,
  cornerSrc,
  grainSrc,
  lowerThird,
  className,
  style,
}: ProsceniumFrameProps) {
  const cornerBase: React.CSSProperties = {
    position: 'absolute',
    width: 120,
    height: 120,
    zIndex: 21,
    pointerEvents: 'none',
    ...(cornerSrc
      ? { backgroundImage: `url(${cornerSrc})`, backgroundSize: 'contain', backgroundRepeat: 'no-repeat' }
      : {
          // Placeholder: gold L-bracket filigree via borders.
          borderTop: '2px solid var(--gold-4, #c9923f)',
          borderLeft: '2px solid var(--gold-4, #c9923f)',
          boxShadow: 'var(--rim-gold, 0 0 24px rgba(230,184,102,.5))',
        }),
  };

  return (
    <div
      className={className}
      style={{ position: 'relative', width: '100%', height: '100%', overflow: 'hidden', ...style }}
    >
      {/* Scene + screen. */}
      <div style={{ position: 'absolute', inset: 0, zIndex: 0 }}>{children}</div>

      {/* Radial vignette darkening the corners. */}
      <div
        aria-hidden
        style={{
          position: 'absolute',
          inset: 0,
          zIndex: 15,
          pointerEvents: 'none',
          background: `radial-gradient(ellipse at center, transparent 55%, rgba(0,0,0,var(--vignette-stage, 0.42)) 100%)`,
        }}
      />

      {/* Film grain (only when real art is supplied). */}
      {grainSrc && (
        <div
          aria-hidden
          style={{
            position: 'absolute',
            inset: 0,
            zIndex: 16,
            pointerEvents: 'none',
            backgroundImage: `url(${grainSrc})`,
            backgroundRepeat: 'repeat',
            opacity: 'var(--grain-opacity, 0.05)',
            mixBlendMode: 'overlay',
          }}
        />
      )}

      {/* Filigree corners (top-left is the source; others mirror it). */}
      <div aria-hidden style={{ ...cornerBase, top: SAFE, left: SAFE }} />
      <div aria-hidden style={{ ...cornerBase, top: SAFE, right: SAFE, transform: 'scaleX(-1)' }} />
      <div aria-hidden style={{ ...cornerBase, bottom: SAFE, left: SAFE, transform: 'scaleY(-1)' }} />
      <div aria-hidden style={{ ...cornerBase, bottom: SAFE, right: SAFE, transform: 'scale(-1, -1)' }} />

      {/* Lower-third slot — pure positioning; LowerThird draws its own box, so
          nothing shows here when no caption is active. */}
      {lowerThird && (
        <div
          style={{
            position: 'absolute',
            left: '8%',
            right: '8%',
            bottom: SAFE,
            zIndex: 25,
          }}
        >
          {lowerThird}
        </div>
      )}
    </div>
  );
}
